import React, { useCallback, useEffect, useState } from "react";
import { BudgetType } from "@akfdev-com/shared/src/models";
import { AccountingCategory } from "@akfdev-com/shared/src/models/AccountingCategory";
import { categoriesApi } from "../../services/api";

/** Map budget types to compatible category types */
const BUDGET_TYPE_TO_CATEGORY_TYPES: Record<
  BudgetType,
  AccountingCategory["categoryType"][]
> = {
  [BudgetType.Income]: ["income"],
  [BudgetType.Expense]: ["expense"],
  [BudgetType.Savings]: ["equity", "asset"],
};

interface CategoryPickerProps {
  /** The selected budget type to filter categories */
  budgetType: BudgetType;
  /** Currently selected category ID */
  value?: string;
  /** Callback when a category is selected */
  onChange: (categoryId: string, categoryName: string) => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Placeholder text when no category is selected */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Error message to display */
  error?: string;
}

const CategoryPicker: React.FC<CategoryPickerProps> = ({
  budgetType,
  value,
  onChange,
  disabled = false,
  placeholder = "Select a category",
  className = "",
  error,
}) => {
  const [categories, setCategories] = useState<AccountingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await categoriesApi.getAll();
      // Filter by budget type compatibility
      const compatibleTypes = BUDGET_TYPE_TO_CATEGORY_TYPES[budgetType];
      const filtered = (Array.isArray(data) ? data : data?.items ?? []).filter(
        (cat: AccountingCategory) =>
          cat.isActive && compatibleTypes.includes(cat.categoryType),
      );
      // Sort by display order
      const sorted = filtered.sort(
        (a: AccountingCategory, b: AccountingCategory) =>
          a.displayOrder - b.displayOrder,
      );
      setCategories(sorted);
    } catch (err) {
      console.error("[CategoryPicker] Failed to load categories:", err);
      setFetchError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, [budgetType]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = e.target.value;
    if (!selectedCode) {
      onChange("", "");
      return;
    }
    const selected = categories.find((c) => c.nominalCode === selectedCode);
    if (selected) {
      onChange(selected.nominalCode, selected.name);
    }
  };

  const selectedCategory = categories.find((c) => c.nominalCode === value);

  return (
    <div className={className}>
      <label
        htmlFor="category-picker"
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        Category
      </label>
      <select
        id="category-picker"
        value={value || ""}
        onChange={handleChange}
        disabled={disabled || loading}
        className={[
          "block w-full rounded-md border-gray-300 shadow-sm",
          "focus:border-primary-500 focus:ring-primary-500 sm:text-sm",
          "disabled:bg-gray-100 disabled:cursor-not-allowed",
          error || fetchError ? "border-red-300" : "border-gray-300",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <option value="">{loading ? "Loading..." : placeholder}</option>
        {categories.map((category) => (
          <option key={category.id} value={category.nominalCode}>
            {category.nominalCode} – {category.name}
          </option>
        ))}
      </select>
      {(error || fetchError) && (
        <p className="mt-1 text-sm text-red-600">{error || fetchError}</p>
      )}
      {selectedCategory && !error && (
        <p className="mt-1 text-xs text-gray-500">
          Selected: {selectedCategory.name} ({selectedCategory.nominalCode})
        </p>
      )}
    </div>
  );
};

export default CategoryPicker;
